import { logger } from "@/modules/logger";
import type { AudioLevelProbe } from "@/modules/audio-recording/legacy";

type SilenceDetectionState = {
  isSilent: boolean;
  silentSince: number | null;
};

type SilenceDetectionManagerDeps = {
  onStateChange: (state: SilenceDetectionState) => void;
};

const SILENCE_THRESHOLD = 0.02;
const SILENCE_DURATION_MS = 60_000;
const FRAME_MODULO = 12;

export class AudioRecordingSilenceDetectionManager {
  private readonly onStateChange: SilenceDetectionManagerDeps["onStateChange"];

  private probe: AudioLevelProbe | null = null;
  private rafId: number | null = null;

  private silenceStartedAt: number | null = null;
  private state: SilenceDetectionState = { isSilent: false, silentSince: null };

  constructor({ onStateChange }: SilenceDetectionManagerDeps) {
    this.onStateChange = onStateChange;
  }

  attachProbe({ audioProbe }: { audioProbe: AudioLevelProbe | null }): void {
    if (this.probe === audioProbe) {
      return;
    }

    this.stopMonitoring();

    this.probe = audioProbe;

    if (!audioProbe) {
      this.resetState();
      return;
    }

    this.resetState();

    if (!this.hasAnimationSupport()) {
      logger.warn({
        message: "[AudioRecordingSilenceDetectionManager] requestAnimationFrame unavailable",
      });
      return;
    }

    const timeDomainData = new Uint8Array(audioProbe.fftSize);
    let frameCounter = 0;

    const tick = () => {
      this.rafId = window.requestAnimationFrame(tick);
      frameCounter += 1;

      if (frameCounter % FRAME_MODULO !== 0) {
        return;
      }

      try {
        audioProbe.getByteTimeDomainData(timeDomainData);
      } catch (error) {
        logger.warn({
          message: "[AudioRecordingSilenceDetectionManager] analyser read failure",
          info: { error: error instanceof Error ? error.message : String(error) },
        });
        return;
      }

      let rms = 0;
      for (let i = 0; i < timeDomainData.length; i += 1) {
        const sample = (timeDomainData[i] - 128) / 128.0;
        rms += sample * sample;
      }
      rms = Math.sqrt(rms / timeDomainData.length);

      const timestamp =
        typeof performance !== "undefined" && typeof performance.now === "function"
          ? performance.now()
          : Date.now();

      this.processSample({ rms, timestamp });
    };

    this.rafId = window.requestAnimationFrame(tick);
  }

  reset(): void {
    this.stopMonitoring();
    this.resetState();
  }

  dispose(): void {
    this.reset();
    this.probe = null;
  }

  private processSample({ rms, timestamp }: { rms: number; timestamp: number }): void {
    if (rms < SILENCE_THRESHOLD) {
      if (this.silenceStartedAt === null) {
        this.silenceStartedAt = timestamp;
      }

      const hasExceededThreshold = timestamp - this.silenceStartedAt >= SILENCE_DURATION_MS;
      if (hasExceededThreshold) {
        this.updateState({ isSilent: true, silentSince: this.silenceStartedAt });
      }
      return;
    }

    this.silenceStartedAt = null;
    this.updateState({ isSilent: false, silentSince: null });
  }

  private updateState(nextState: SilenceDetectionState): void {
    if (
      this.state.isSilent === nextState.isSilent &&
      this.state.silentSince === nextState.silentSince
    ) {
      return;
    }

    this.state = nextState;
    this.onStateChange(this.state);
  }

  private resetState(): void {
    this.silenceStartedAt = null;
    this.updateState({ isSilent: false, silentSince: null });
  }

  private stopMonitoring(): void {
    if (this.rafId === null) {
      return;
    }

    if (this.hasAnimationSupport()) {
      window.cancelAnimationFrame(this.rafId);
    }

    this.rafId = null;
  }

  private hasAnimationSupport(): boolean {
    return (
      typeof window !== "undefined" &&
      typeof window.requestAnimationFrame === "function" &&
      typeof window.cancelAnimationFrame === "function"
    );
  }
}
